/**
 * カーソル型ページングの汎用フック。
 *
 * - items は append-only で伸びる (データ配列は成長する)
 * - DOM 側の制約は VirtualFeed が担当する
 * - deps が変わると state をリセットして再フェッチ
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Error と (再帰的に) cause を console に吐き出す。
 * `setErr(String(e.message))` だけでは TokenRefreshError などラッパー型の
 * 中身 (status / response / 内部の Error.stack) が消えるので、この hook 由来の
 * 失敗は `console.error('[infinite-feed] ...', e, { causeChain })` で残す。
 *
 * オーバーヘッドは catch 時のみ。本番ノイズにならない範囲。
 */
function logFeedError(label: string, e: unknown): void {
  const causes: unknown[] = [];
  let cur: unknown = (e as { cause?: unknown })?.cause;
  // 循環参照しない範囲で 5 段まで
  for (let i = 0; i < 5 && cur; i++) {
    causes.push(cur);
    cur = (cur as { cause?: unknown })?.cause;
  }
  console.error(`[infinite-feed] ${label}`, {
    error: e,
    name: (e as Error)?.name,
    message: (e as Error)?.message,
    stack: (e as Error)?.stack,
    causes,
    timestamp: new Date().toISOString(),
  });
}

export interface InfiniteFeedPage<T> {
  items: T[];
  cursor?: string;
}

export interface UseInfiniteFeedOptions<T> {
  fetchPage: (cursor?: string) => Promise<InfiniteFeedPage<T>>;
  /** dedupe に使うキー抽出関数 */
  keyOf: (item: T) => string;
  /** 依存値が変わるとリセット */
  deps: ReadonlyArray<unknown>;
  /** 有効化フラグ (false の間は何もしない) */
  enabled?: boolean;
  /** SWR キャッシュ。指定すると hydrate (load) と保存 (save) を mount 時 / load(reset) 成功時に行う。
   *  未指定なら現状動作 (毎回 network から fresh) のまま。 */
  cache?: {
    load: () => Promise<T[] | undefined>;
    save: (items: T[]) => Promise<void>;
  };
}

export interface InfiniteFeedState<T> {
  items: T[];
  loading: boolean;
  err: string | null;
  done: boolean;
  loadMore: () => void;
  /** 先頭から再読み込み (投稿直後のリフレッシュ用など) */
  refresh: () => void;
}

export function useInfiniteFeed<T>(opts: UseInfiniteFeedOptions<T>): InfiniteFeedState<T> {
  const { fetchPage, keyOf, deps, enabled = true, cache } = opts;

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const cursorRef = useRef<string | undefined>(undefined);
  const inflight = useRef(false);
  const doneRef = useRef(false);
  // 連続で「ほぼ重複ページ」(新規が少ない) を踏んだ回数。Bluesky が cursor 付きで
  // 重複だらけのページを返し続けるケースで loadMore 暴走 (→ モバイル OOM) を止める。
  const lowProgressRef = useRef(0);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const keyOfRef = useRef(keyOf);
  keyOfRef.current = keyOf;
  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  // done 判定 (新規ゼロ検出) 用に、コミット済みの items を ref で参照する。
  // 件数カウントにのみ使う (state 更新は functional updater のまま = refresh と競合しない)。
  const itemsRef = useRef<T[]>([]);
  itemsRef.current = items;
  // hydrate epoch: deps 変化ごとに増やすことで、古い cache.load() の解決が
  // 新しい hydrate を上書きしないようにする (race ガード)
  const hydrateEpoch = useRef(0);

  const load = useCallback(async (resetting: boolean) => {
    if (inflight.current) return;
    if (!enabled) return;
    if (!resetting && doneRef.current) return;
    inflight.current = true;
    setLoading(true);
    setErr(null);
    try {
      const nextCursor = resetting ? undefined : cursorRef.current;
      const page = await fetchPageRef.current(nextCursor);
      // 新規追加件数を、コミット済み items に対してカウントする (done 判定用)。
      const baseForCount = resetting ? [] : itemsRef.current;
      const seenForCount = new Set(baseForCount.map((x) => keyOfRef.current(x)));
      let added = 0;
      for (const it of page.items) {
        const k = keyOfRef.current(it);
        if (!seenForCount.has(k)) {
          seenForCount.add(k);
          added += 1;
        }
      }
      setItems((prev) => {
        const base = resetting ? [] : prev;
        const seen = new Set(base.map((x) => keyOfRef.current(x)));
        const merged = [...base];
        for (const it of page.items) {
          const k = keyOfRef.current(it);
          if (!seen.has(k)) {
            merged.push(it);
            seen.add(k);
          }
        }
        return merged;
      });
      cursorRef.current = page.cursor;
      // 低進捗バックオフ: append fetch で「ページの大半が重複」(新規が少ない) のが
      // 連続したら done にする。Bluesky が cursor 付きで重複だらけのページを返し
      // 続けるケースで、onEndReached → loadMore が空振りフェッチ (30 件取得 + パース)
      // を連打し、ネットワーク/メモリのチャーンでモバイルが OOM するのを防ぐ。
      // 健全な TL は毎ページ大量の新規が来るので発火しない。
      if (!resetting && page.items.length > 0) {
        const lowThreshold = Math.max(3, Math.floor(page.items.length * 0.3));
        if (added < lowThreshold) lowProgressRef.current += 1;
        else lowProgressRef.current = 0;
      }
      // done にする条件: 正規の終端 (cursor 無し / 空ページ) か、
      // 低進捗が 2 連続 (= これ以上 loadMore しても新規はほぼ無い)。
      if (!page.cursor || page.items.length === 0 || lowProgressRef.current >= 2) {
        doneRef.current = true;
        setDone(true);
      }
      // SWR: load(reset) 成功時に直近 1 ページを cache に保存。
      // append load 時は保存しない (1 ページ目のみキャッシュする方針)。
      if (resetting && cacheRef.current && page.items.length > 0) {
        void cacheRef.current.save(page.items);
      }
    } catch (e) {
      logFeedError(resetting ? 'load(reset) failed' : 'load(append) failed', e);
      setErr(String((e as Error)?.message ?? e));
    } finally {
      inflight.current = false;
      setLoading(false);
    }
  }, [enabled]);

  // deps 変化でリセット + (cache あれば) hydrate
  useEffect(() => {
    setItems([]);
    cursorRef.current = undefined;
    doneRef.current = false;
    lowProgressRef.current = 0;
    setDone(false);
    setErr(null);
    if (!enabled) return;
    const epoch = ++hydrateEpoch.current;
    // SWR: cache から hydrate を非同期で発射 (network fetch を待たせない)
    if (cacheRef.current) {
      void cacheRef.current.load().then((cached) => {
        if (!cached || cached.length === 0) return;
        // 自分が最新の hydrate でなくなっていたら無視 (deps 変化で次の epoch に進んだ場合)
        if (epoch !== hydrateEpoch.current) return;
        // fresh が先に到達して items が埋まっていたら上書きしない
        setItems((curr) => (curr.length === 0 ? cached : curr));
      });
    }
    // 並行で fresh fetch を開始。cached より遅れて到達して items を置換する。
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);

  const loadMore = useCallback(() => {
    if (!inflight.current && !doneRef.current) void load(false);
  }, [load]);

  /**
   * 先頭ページを取得して既存と「マージ」する。
   * 画面から投稿が一瞬消えるのを避けるため、items は clear せず、
   * 新しい投稿だけを先頭に挿入する。既存のカーソル (末尾) はそのまま維持。
   */
  const refresh = useCallback(() => {
    if (inflight.current || !enabled) return;
    inflight.current = true;
    setLoading(true);
    setErr(null);
    fetchPageRef.current(undefined)
      .then((page) => {
        // page.items 自体に重複キーが含まれる可能性がある (Bluesky の edge case) ので
        // まず new 内で dedupe、その後 prev から new に含まれる key を除外。
        setItems((prev) => {
          const seen = new Set<string>();
          const uniqueNew: T[] = [];
          for (const it of page.items) {
            const k = keyOfRef.current(it);
            if (!seen.has(k)) {
              seen.add(k);
              uniqueNew.push(it);
            }
          }
          const kept = prev.filter((x) => !seen.has(keyOfRef.current(x)));
          return [...uniqueNew, ...kept];
        });
      })
      .catch((e) => {
        logFeedError('refresh failed', e);
        setErr(String((e as Error)?.message ?? e));
      })
      .finally(() => {
        inflight.current = false;
        setLoading(false);
      });
  }, [enabled]);

  return { items, loading, err, done, loadMore, refresh };
}
